/**
 * PWA wiring: registers the service worker and captures
 * beforeinstallprompt so the AppShell can offer an "Install" CTA.
 *
 * Hook returns:
 *   { canInstall, promptInstall, installed, standalone }
 *
 * - `standalone` is true when the page is launched from the installed PWA
 *   (display-mode: standalone OR Apple's navigator.standalone shim).
 * - `canInstall` is true when the browser fired beforeinstallprompt and the
 *   user hasn't accepted/dismissed yet (Chrome/Edge/Brave). On iOS Safari
 *   it stays false — those users install via the share-sheet.
 */
import { useEffect, useState } from "react";

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
};

let cached: BeforeInstallPromptEvent | null = null;
const listeners = new Set<(ev: BeforeInstallPromptEvent | null) => void>();

if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    cached = e as BeforeInstallPromptEvent;
    listeners.forEach((l) => l(cached));
  });
  window.addEventListener("appinstalled", () => {
    cached = null;
    listeners.forEach((l) => l(null));
  });
}

export function registerServiceWorker() {
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return;
  if (window.location.hostname === "localhost") return; // Vite dev is HMR; SW would interfere
  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/sw.js", { scope: "/" }).catch((err) => {
      console.warn("[pwa] sw register failed:", err?.message ?? err);
    });
  });
}

function getStandalone(): boolean {
  if (typeof window === "undefined") return false;
  const mq = window.matchMedia?.("(display-mode: standalone)").matches;
  const ios = (window.navigator as { standalone?: boolean }).standalone === true;
  return Boolean(mq || ios);
}

export function usePwa() {
  const [evt, setEvt] = useState<BeforeInstallPromptEvent | null>(cached);
  const [standalone, setStandalone] = useState(getStandalone());

  useEffect(() => {
    const fn = (e: BeforeInstallPromptEvent | null) => setEvt(e);
    listeners.add(fn);
    return () => {
      listeners.delete(fn);
    };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(display-mode: standalone)");
    const upd = () => setStandalone(getStandalone());
    mq.addEventListener?.("change", upd);
    return () => mq.removeEventListener?.("change", upd);
  }, []);

  async function promptInstall() {
    if (!evt) return { outcome: "unavailable" as const };
    await evt.prompt();
    const choice = await evt.userChoice;
    cached = null;
    setEvt(null);
    return choice;
  }

  return {
    canInstall: !!evt,
    promptInstall,
    standalone,
  };
}
